import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Pill } from "../common/Pill";
import type { CheckMapEntry, CostState } from "../../types";
import type { SAStatus } from "../../sa";

export interface CheckToggleEntry extends CheckMapEntry {
  id: string;
}

export interface CheckTogglesProps {
  toggledOff: Set<string>;
  toggleCheck: (id: string) => void;
  thresholds: Record<string, number>;
  setThreshold: (id: string, v: number) => void;
  firmThresholds: Record<string, number> | null;
  saveFirmThresholds: () => Promise<boolean>;
  applyFirmThresholds: () => void;
  saStatus: SAStatus;
  checks: CheckToggleEntry[];
  readOnly?: boolean;
  cost: CostState;
}

/* The "Checks" tab — list each check with its τ control, on/off, and prompt
   expander; plus "save current τ as firm defaults" against the SA service. */
export function CheckToggles({
  toggledOff, toggleCheck, thresholds, setThreshold, firmThresholds,
  saveFirmThresholds, applyFirmThresholds, saStatus, checks, readOnly, cost,
}: CheckTogglesProps): JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const fired = cost && cost.perCheck ? new Set(Object.keys(cost.perCheck)) : null;
  const onSave = async (): Promise<void> => {
    const ok = await saveFirmThresholds();
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    }
  };
  const dirty = firmThresholds
    ? checks.some(
        (c) =>
          typeof firmThresholds[c.id] === "number" &&
          (thresholds[c.id] ?? c.tau) !== firmThresholds[c.id],
      )
    : false;
  return (
    <div>
      <div className="up" style={{ marginBottom: 8 }}>
        {readOnly ? "Checks used · τ · prompt" : "Per-run · toggle, tune τ, inspect prompt"}
      </div>
      {checks.map((c) => {
        const on = !toggledOff.has(c.id);
        const ex = open === c.id;
        const didFire = fired ? fired.has(c.id) : true;
        const unfired = on && !didFire;
        return (
          <div
            key={c.id}
            style={{ borderBottom: "1px solid var(--bd)" }}
            title={unfired ? "Did not fire on this corpus." : undefined}
          >
            <div className="ctgl" style={{ borderBottom: "none", opacity: on ? 1 : 0.5 }}>
              <input
                type="checkbox"
                className="chk"
                checked={on}
                disabled={readOnly}
                onChange={() => !readOnly && toggleCheck(c.id)}
              />
              <div
                className="cn"
                style={{ cursor: "pointer", minWidth: 0 }}
                onClick={() => setOpen(ex ? null : c.id)}
              >
                <div
                  className="cnm"
                  style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
                >
                  {c.name}
                  {unfired && (
                    <span
                      className="pill"
                      style={{ background: "var(--subtle)", color: "var(--tert)", flex: "none" }}
                      title="Did not fire on this corpus."
                    >
                      didn't fire
                    </span>
                  )}
                </div>
                <div className="cds">{c.desc}</div>
              </div>
              <span
                className="ttrigger"
                data-tip="Threshold above which the check fires. Calibrate to your firm's tolerance."
              >
                <input
                  className="inp numin"
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  value={thresholds[c.id] ?? c.tau}
                  disabled={readOnly}
                  onChange={(e) =>
                    !readOnly && setThreshold(c.id, parseFloat(e.target.value) || 0)
                  }
                  aria-label={"Threshold τ for " + c.name}
                />
              </span>
              <span className="icon-btn" onClick={() => setOpen(ex ? null : c.id)}>
                {ex ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </span>
            </div>
            {ex && (
              <div className="cdetail" style={{ margin: "0 0 10px" }}>
                {unfired && (
                  <>
                    <div className="lbl">Status</div>
                    <div className="pp" style={{ marginBottom: 8, color: "var(--tert)" }}>
                      Did not fire on this corpus — no email tripped the threshold τ=
                      {(thresholds[c.id] ?? c.tau).toFixed(2)}. Lower τ to make it more sensitive,
                      or assemble a corpus that exercises this case in the Library.
                    </div>
                  </>
                )}
                {c.threshold_rationale && (
                  <>
                    <div className="lbl">Why τ = {(thresholds[c.id] ?? c.tau).toFixed(2)}</div>
                    <div
                      className="pp"
                      style={{
                        whiteSpace: "normal",
                        fontFamily: "var(--sans)",
                        fontSize: 12,
                        marginBottom: 8,
                      }}
                    >
                      {c.threshold_rationale}
                    </div>
                  </>
                )}
                <div className="lbl">Prompt template</div>
                <div className="pp" style={{ marginBottom: 0 }}>
                  {c.prompt}
                </div>
              </div>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <div className="tert" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>
          Run-local overrides. Lowering τ makes a detector fire more readily; raising it above a
          result suppresses that detection on future emails. Edit the canonical set under
          Library → Agent Registry.
        </div>
      )}
      {!readOnly && saStatus === "online" && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--bd)" }}>
          <div className="up" style={{ marginBottom: 6 }}>
            Firm default τ{" "}
            {firmThresholds ? (
              <Pill color="--green" label="saved" />
            ) : (
              <Pill color="--low" label="not set" />
            )}
          </div>
          <div className="tert" style={{ fontSize: 11.5, marginBottom: 8, lineHeight: 1.5 }}>
            Persisted in the Senior Associate service (<span className="mono">:8100/config</span>)
            and used to seed new runs.{" "}
            {saved && <span style={{ color: "var(--green)" }}>Saved ✓</span>}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="sel"
              style={{ cursor: "pointer", fontSize: 11.5, padding: "4px 10px" }}
              onClick={onSave}
            >
              {firmThresholds ? "Update firm defaults" : "Save current τ as firm defaults"}
            </button>
            <button
              className="sel"
              style={{
                cursor: firmThresholds ? "pointer" : "default",
                fontSize: 11.5,
                padding: "4px 10px",
                opacity: firmThresholds && dirty ? 1 : 0.5,
              }}
              disabled={!firmThresholds || !dirty}
              onClick={applyFirmThresholds}
            >
              Restore firm defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
